import React from 'react';
import PaletteOutlinedIcon from '@mui/icons-material/PaletteOutlined';
import MenuOutlinedIcon from '@mui/icons-material/MenuOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import RemoveShoppingCartOutlinedIcon from '@mui/icons-material/RemoveShoppingCartOutlined';
import { ROUTES } from 'config/routes';

export type AdminNavLink = {
  title: string;
  description: string;
  to: string;
};

export type AdminNavGroup = {
  id: string;
  title: string;
  description: string;
  icon: React.ElementType;
  /** Destino directo. Si está definido, el grupo es un botón y no despliega `links`. */
  to?: string;
  links: AdminNavLink[];
};

export const ADMIN_NAV_GROUPS: AdminNavGroup[] = [
  {
    id: 'operation',
    title: 'Operación',
    description: 'Seguimiento comercial de la tienda.',
    icon: RemoveShoppingCartOutlinedIcon,
    links: [
      {
        title: 'Carritos abandonados',
        description: 'Clientes que dejaron productos en el carrito.',
        to: ROUTES.ADMIN_ABANDONED_CARTS,
      },
      {
        title: 'Solicitudes de acceso',
        description: 'Aprobá o rechazá el ingreso de clientes.',
        to: ROUTES.ADMIN_ACCESS_REQUESTS,
      },
    ],
  },
  {
    id: 'catalog',
    title: 'Catálogo',
    description: 'Qué productos se publican y cómo se muestran.',
    icon: Inventory2OutlinedIcon,
    to: ROUTES.ADMIN_CATALOG,
    links: [],
  },
  {
    id: 'design',
    title: 'Diseño y marca',
    description: 'Cómo se ve la tienda.',
    icon: PaletteOutlinedIcon,
    links: [
      { title: 'Apariencia', description: 'Logo, marca de agua, colores, contacto y redes sociales.', to: ROUTES.ADMIN_APPEARANCE },
      { title: 'Layout de la home', description: 'Banners y bloques de la página de inicio.', to: ROUTES.ADMIN_LAYOUT },
      { title: 'Página de ingreso', description: 'Textos, logo, fondo y enlaces de /ingresar.', to: ROUTES.ADMIN_AUTH_PAGE },
      { title: 'Diseño de tarjeta', description: 'Cómo se ve cada producto en el catálogo.', to: ROUTES.ADMIN_CARD_DESIGN },
      { title: 'Diseño de página de producto', description: 'Plantilla y datos de la ficha de producto.', to: ROUTES.ADMIN_PRODUCT_PAGE_DESIGN },
    ],
  },
  {
    id: 'navigation',
    title: 'Navegación',
    description: 'Cómo se recorre la tienda.',
    icon: MenuOutlinedIcon,
    links: [
      { title: 'Header y footer', description: 'Menú superior y enlaces del pie de página.', to: ROUTES.ADMIN_NAVIGATION },
      { title: 'Páginas', description: 'Páginas de contenido propias de la tienda.', to: ROUTES.ADMIN_PAGES },
    ],
  },
  {
    id: 'system',
    title: 'Sistema',
    description: 'Opciones avanzadas.',
    icon: TuneOutlinedIcon,
    links: [
      { title: 'Funcionalidades', description: 'Activá o desactivá opciones de la tienda.', to: ROUTES.ADMIN_FEATURES },
      { title: 'Migración de configuración', description: 'Pack ZIP para pasar de test a producción.', to: ROUTES.ADMIN_CONFIG_PACK },
    ],
  },
];

const parseLink = (to: string) => {
  const [path, query = ''] = to.split('?');
  return { path, params: new URLSearchParams(query) };
};

/**
 * Devuelve el `to` del enlace que corresponde a la URL actual. Si varios coinciden
 * (ej. "Productos" y "Fotos"), gana el que tiene más parámetros en común.
 */
export const findActiveAdminLink = (pathname: string, search: string): string | null => {
  const current = new URLSearchParams(search);
  let best: { to: string; score: number } | null = null;
  for (const group of ADMIN_NAV_GROUPS) {
    for (const link of group.links) {
      const { path, params } = parseLink(link.to);
      if (path !== pathname) continue;
      const keys = Array.from(params.keys());
      const matches = keys.length === 0
        ? !current.has('tab')
        : keys.every((key) => current.get(key) === params.get(key));
      if (!matches) continue;
      if (!best || keys.length > best.score) best = { to: link.to, score: keys.length };
    }
  }
  return best?.to ?? null;
};
